import React from 'react';
import ContentBox from '../components/ContentBox';

function PrivacyPolicy() {
    const lastUpdated = '30 April 2026';

    const sections = [
        {
            title: 'What We Collect',
            content: [
                'We only collect information that is strictly necessary to operate Frankly Origins. This includes:',
                [
                    'Your Discord ID, used to identify your account and manage your session.',
                    'Your Habbo username, shared with us by OriginsBot during the login process and used to display your profile.',
                    'Your Discord server roles, checked at login to determine staff or admin access.',
                    'Any content you voluntarily submit, such as article comments or profile information.',
                ],
                'We do not collect your real name, address, phone number, or any payment information. We do not store passwords.',
            ],
        },
        {
            title: 'Analytics',
            content: [
                'Frankly Origins uses Umami Analytics to understand how visitors use the site. Umami is a privacy-focused analytics tool that does not use cookies and does not store any personally identifiable information.',
                'Umami does not track individuals across websites or sessions. Approximate location data such as country and city is derived from anonymised IP addresses using a general geolocation lookup. Raw IP addresses are never stored.',
                'Because Umami does not use cookies and does not collect personal data, no cookie consent banner is required. All analytics data is hosted on our own infrastructure and is never shared with third parties.',
            ],
        },
        {
            title: 'How We Log You In',
            content: [
                'Frankly Origins does not use a traditional username and password system. Instead, we use Discord OAuth via OriginsBot as our authentication method.',
                'When you log in, you are redirected to Discord to authorise OriginsBot. Once authorised, OriginsBot shares your Discord ID and your verified Habbo username with us. We use this information to identify your account.',
                'We never see or store your Discord password. Authentication is handled entirely by Discord.',
            ],
        },
        {
            title: 'Cookies and Sessions',
            content: [
                'Frankly Origins uses cookies solely for session management. When you log in, two secure cookies are set in your browser:',
                [
                    'An access token cookie, which expires after 8 hours and is used to authenticate your requests while you browse the site.',
                    'A refresh token cookie, which lasts up to 7 days and is used to automatically issue a new access token when yours expires, keeping you logged in without requiring you to sign in again.',
                ],
                'Both cookies are marked as HttpOnly and Secure, meaning they cannot be accessed by JavaScript and are only sent over HTTPS. Refresh tokens are stored in our database and can be revoked at any time by logging out.',
                'We do not use cookies for advertising, tracking, or analytics. We do not use any third party tracking cookies.',
            ],
        },
        {
            title: 'How We Use Your Data',
            content: [
                'The data we hold is used exclusively to:',
                [
                    'Authenticate you when you log in via Discord.',
                    'Display your Habbo profile and activity on the site.',
                    'Allow you to participate in site features such as commenting, framp collection, and the imager.',
                    'Determine your access level based on your Discord roles.',
                    'Send you in-site messages from the Frankly team where relevant.',
                ],
                'We do not sell, rent, or share your personal data with any third party.',
            ],
        },
        {
            title: 'Image Uploads and AI Moderation',
            content: [
                'Frankly Origins includes a feature called FranklyCloud, which allows logged-in users to upload images to their personal cloud storage on the site. Uploaded images are stored on our own servers.',
                'Before any uploaded image is accepted and saved, it is transmitted to a third-party AI moderation service provided by Hugging Face, Inc. This service analyses the image to assess whether its content is appropriate for the site. The image data is sent over an encrypted connection.',
                'We use this moderation step to help enforce our Community Guidelines and maintain a safe environment on the site. Images flagged as inappropriate are rejected and are not stored on our servers.',
                'The AI moderation service is operated by Hugging Face and is subject to their own terms and privacy policy. We do not retain or store images on Hugging Face infrastructure beyond what is necessary to perform the moderation check.',
                'Uploaded images that pass moderation are stored on our servers and may be visible to other users where they have been shared or embedded in site content. They are also accessible to site administrators for moderation purposes.',
                'You can delete your own uploaded images at any time from within FranklyCloud. Deleted images are permanently removed from our servers.',
            ],
        },
        {
            title: 'Data Retention',
            content: [
                'Your data is retained for as long as your account is active. Expired refresh tokens are periodically cleaned from our database. If you wish to have your account and associated data removed, you can contact us at hello@franklyorigins.net and we will action your request promptly.',
            ],
        },
        {
            title: 'Third Party Services',
            content: [
                'Frankly Origins interacts with the official Habbo Origins API to retrieve avatar figure strings and validate usernames. This is a read only lookup using publicly available Habbo data.',
                'Login is handled via Discord OAuth. When you initiate a login, you will be directed to Discord, where their own privacy policy applies. We receive only the information described above from this process.',
                'Our site is hosted on external infrastructure. Hosting providers may have access to server logs as part of normal operations, but these are not used to identify individual users.',
            ],
        },
        {
            title: 'Security',
            content: [
                'We take reasonable technical measures to protect your data, including:',
                [
                    'All connections to Frankly Origins are served over HTTPS.',
                    'Session cookies are HttpOnly and Secure, protecting them against common web vulnerabilities.',
                    'Refresh tokens are stored in our database and invalidated on logout.',
                    'Access tokens expire after 15 minutes, limiting exposure if intercepted.',
                ],
                'No system is perfectly secure. If you believe your account has been compromised, please contact us immediately.',
            ],
        },
        {
            title: 'Your Rights',
            content: [
                'You have the right to access the data we hold about you, request corrections, or ask for deletion of your account and data. To exercise any of these rights, please contact us at hello@franklyorigins.net.',
            ],
        },
        {
            title: 'Changes to This Policy',
            content: [
                'We may update this Privacy Policy from time to time. When we do, the "Last Updated" date at the top of this page will reflect the change. Continued use of Frankly Origins after any changes constitutes acceptance of the updated policy.',
            ],
        },
        {
            title: 'Contact',
            content: [
                'If you have any questions about this Privacy Policy or how we handle your data, please reach out to us at hello@franklyorigins.net.',
            ],
        },
    ];

    const renderContent = (content) =>
        content.map((item, i) => {
            if (Array.isArray(item)) {
                return (
                    <ul key={i} className="list-disc list-inside space-y-1 ml-2 mt-1 mb-2">
                        {item.map((li, j) => (
                            <li key={j} className="text-gray-700 text-sm leading-relaxed">
                                {li}
                            </li>
                        ))}
                    </ul>
                );
            }
            return (
                <p key={i} className="text-gray-700 text-sm leading-relaxed mb-2">
                    {item}
                </p>
            );
        });

    return (
        <div className="w-full min-h-screen p-4 lg:p-6">
            <ContentBox theme="default" tint="lightskyblue" header="Privacy Policy">
                <div className="mb-4 pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                        Last Updated: {lastUpdated}
                    </p>
                    <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                        At Frankly Origins, we respect your privacy and are committed to being transparent about the limited data we collect. This policy explains what we store, why we store it, and how it is used.
                    </p>
                </div>

                <div className="space-y-5">
                    {sections.map((section, i) => (
                        <div key={i}>
                            <h3 className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-maroon mb-2">
                                {i + 1}. {section.title}
                            </h3>
                            <div>{renderContent(section.content)}</div>
                        </div>
                    ))}
                </div>
            </ContentBox>
        </div>
    );
}

export default PrivacyPolicy;